// PARA MIRAR — ¿hay algo que tenga que revisar?
//
// Este bloque no existe en la pantalla actual, y es el hueco más grande que
// encontró el relevamiento: hoy el dueño tiene que salir a cazar el problema.
// Entra, mira 35 gráficos, y trata de darse cuenta solo de si algo se salió de
// lo normal esta semana.
//
// Va en segundo lugar, después de "Cómo va hoy", porque es lo que convierte al
// Monitor de una pantalla que se consulta en una que avisa.
//
// Usa la anatomía común (Bloque.js) como todos los demás. Lo único propio que
// conserva es el color del acento, que cambia según haya avisos o no: rojo
// cuando hay algo que puede costar plata, verde cuando se revisó y está todo
// bien. Ese color entra por la prop `acento`, no por un encabezado a mano.
//
// Tres decisiones de presentación que importan tanto como el cálculo (que vive
// en reglas.js):
//
// · CERO SEÑALES ES UNA RESPUESTA, no un vacío. "Revisé y no hay nada raro"
//   es exactamente lo que el dueño vino a saber, y hay que decírselo.
//
// · CADA SEÑAL TRAE SU MATIZ. Debajo del titular va la razón por la que puede
//   NO ser un problema. Sin eso, el cliente que anula tickets por motivos
//   legítimos recibe una acusación, deja de creerle a la pantalla, y el día que
//   la señal importa de verdad ya no la lee.
//
// · NINGUNA SEÑAL ES UN CALLEJÓN SIN SALIDA. La que se puede investigar trae
//   el link a la pantalla donde están los casos uno por uno. La señal dice
//   QUÉ; la otra pantalla dice QUIÉN y POR QUÉ.

import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import Bloque from "./Bloque";
import { UI, ESTADO, POLARIDAD, ANCHO_LECTURA } from "./theme";
import { formatCurrency, fecha } from "./format";
import { evaluarSenales, MINIMO_HISTORIA, VENTANA_SENALES } from "./reglas";

const ESTILO = {
  aviso: { color: ESTADO.alerta.color, fondo: "#fdecec", icono: "priority_high" },
  mirar: { color: ESTADO.anulado.color, fondo: "#fff8e6", icono: "visibility" },
  info: { color: UI.textoSuave, fondo: UI.fondoSuave, icono: "info" },
};

const Senal = ({ s }) => {
  const e = ESTILO[s.nivel] || ESTILO.info;
  return (
    <div
      style={{
        display: "flex", gap: 11, alignItems: "flex-start",
        background: e.fondo, borderRadius: 8, padding: "11px 13px",
        borderLeft: `3px solid ${e.color}`,
      }}
    >
      <span className="material-symbols-rounded" style={{ fontSize: 20, color: e.color, flexShrink: 0, marginTop: 1 }}>
        {e.icono}
      </span>
      <div style={{ minWidth: 0, maxWidth: ANCHO_LECTURA }}>
        <div style={{ fontWeight: 700, color: UI.texto, fontSize: 14, lineHeight: 1.35 }}>
          {s.titular}
          {typeof s.monto === "number" && (
            <span style={{ color: e.color }}> · {formatCurrency(s.monto)}</span>
          )}
        </div>
        {s.detalle && (
          <div style={{ fontSize: 13, color: UI.texto, marginTop: 3, lineHeight: 1.4 }}>{s.detalle}</div>
        )}
        {/* El matiz NO es letra chica opcional: es lo que evita que la señal
            se lea como una acusación. Ver la nota de arriba. */}
        {s.matiz && (
          <div style={{ fontSize: 12, color: UI.textoSuave, marginTop: 4, lineHeight: 1.4 }}>{s.matiz}</div>
        )}
        {s.verEn?.ruta && (
          <Link to={s.verEn.ruta} style={{ fontSize: 12.5, fontWeight: 600, display: "inline-block", marginTop: 6 }}>
            {s.verEn.texto}
            <span className="material-symbols-rounded ms-1" style={{ fontSize: 14, verticalAlign: "middle" }}>
              arrow_forward
            </span>
          </Link>
        )}
      </div>
    </div>
  );
};

const ParaMirar = ({ cerrados = [], abonados = [] }) => {
  const { senales, suficiente, faltan } = useMemo(
    () => evaluarSenales({ cerrados, abonados }),
    [cerrados, abonados]
  );

  // El rango REAL de la semana evaluada, con fechas. "Últimos 7 días" puede ser
  // una semana de calendario con dos días caídos, y no es lo mismo.
  const rango = useMemo(() => {
    const semana = cerrados.slice(-VENTANA_SENALES).filter((d) => d?.date);
    if (!semana.length) return null;
    const desde = fecha(semana[0].date);
    const hasta = fecha(semana[semana.length - 1].date);
    const previos = Math.max(0, cerrados.length - semana.length);
    return `${desde} – ${hasta} · comparado contra tus ${previos} días anteriores`;
  }, [cerrados]);

  // Un cliente nuevo no puede recibir señales calculadas sobre su propia
  // historia, porque todavía no la tiene. Se le dice, en vez de mostrarle un
  // bloque vacío que parece roto.
  if (!suficiente) {
    return (
      <Bloque
        nombre="Para mirar"
        id="para-mirar"
        titular="Todavía no puedo avisarte si algo se sale de lo normal"
        frase={`Los avisos se calculan comparando tu semana contra tu propia historia, así que necesitan al menos ${MINIMO_HISTORIA} días con datos. Te faltan ${faltan}. Se completa solo: cada jornada que TicketControl cierra suma un día.`}
        rango={`${cerrados.length} de los ${MINIMO_HISTORIA} días que hacen falta`}
      />
    );
  }

  const hayAviso = senales.some((s) => s.nivel === "aviso");

  // Cero señales no es un vacío: es la respuesta que el dueño vino a buscar. Se
  // dice explícitamente, y se dice QUÉ se revisó.
  const titular = senales.length === 0
    ? "Revisé la semana y no hay nada raro"
    : `Hay ${senales.length} ${senales.length === 1 ? "cosa" : "cosas"} para mirar esta semana`;

  const frase = senales.length === 0
    ? "Miré anulaciones, movimientos marcados, caídas de facturación, saldos de abonados y días sin datos. Todo está dentro de lo normal para tu playa."
    : "Cada aviso se compara contra lo que es normal en tu propia playa, no contra un número fijo. Debajo de cada uno está la razón por la que puede no ser un problema.";

  return (
    <Bloque
      nombre="Para mirar"
      id="para-mirar"
      titular={titular}
      frase={frase}
      rango={rango}
      acento={hayAviso ? ESTADO.alerta.color : POLARIDAD.entra.color}
    >
      {senales.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {senales.map((s) => <Senal key={s.id} s={s} />)}
        </div>
      )}
    </Bloque>
  );
};

export default ParaMirar;
